<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { INPUT_TYPES } from 'dashboard/components-next/taginput/helper/tagInputHelper.js';
import {
  DEFAULT_COUNTRY,
  formatNationalPhone,
  isComposablePhone,
  looksLikePhone,
  normalizePhone,
} from 'shared/helpers/smarticksPhone';

import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contacts: {
    type: Array,
    required: true,
  },
  selectedContact: {
    type: Object,
    default: null,
  },
  showContactsDropdown: {
    type: Boolean,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  isCreatingContact: {
    type: Boolean,
    required: true,
  },
  contactId: {
    type: String,
    default: null,
  },
  contactableInboxesList: {
    type: Array,
    default: () => [],
  },
  showInboxesDropdown: {
    type: Boolean,
    required: true,
  },
  hasErrors: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'searchContacts',
  'setSelectedContact',
  'clearSelectedContact',
  'updateDropdown',
]);

const i18nPrefix = 'COMPOSE_NEW_CONVERSATION.FORM.CONTACT_SELECTOR';
const { t } = useI18n();

const inputType = ref(INPUT_TYPES.EMAIL);
const searchQuery = ref('');
// true once the search for the CURRENT query has resolved: `isLoading=false && contacts=[]`
// is also the initial state, and the search is debounced 400 ms
const hasSearched = ref(false);
watch(
  () => props.isLoading,
  (loading, wasLoading) => {
    if (wasLoading && !loading) hasSearched.value = true;
  }
);

const contactsList = computed(() => {
  return props.contacts?.map(({ name, id, thumbnail, email, ...rest }) => ({
    id,
    label: email ? `${name} (${email})` : name,
    value: id,
    thumbnail: { name, src: thumbnail },
    ...rest,
    name,
    email,
    action: 'contact',
  }));
});

// Smarticks: a typed local mobile ("052-545-7077") that matched no contact gets an explicit
// "create new contact" row. Supplying it here (not in TagInput) keeps TagInput generic; the
// row carries `phoneNumber`, so TagInput validates it as TEL and emits action 'create'.
// Only after the search resolved empty, and only for a composable number (a 9-digit ID is
// also a valid landline shape -- no create row for it).
const createContactItem = computed(() => {
  if (!hasSearched.value || props.isLoading || props.contacts?.length)
    return [];
  const query = searchQuery.value.trim();
  const normalized = isComposablePhone(query)
    ? normalizePhone(query, DEFAULT_COUNTRY)
    : null;
  if (!normalized) return [];
  return [
    {
      id: 'smarticks-create-contact',
      label: t(`${i18nPrefix}.CREATE_CONTACT`, {
        number: formatNationalPhone(normalized),
        e164: normalized,
      }),
      value: query,
      phoneNumber: query,
      icon: 'i-lucide-user-plus',
      action: 'create',
    },
  ];
});

// in 'tel' mode an email-only result is unselectable (TagInput validates the tag as a phone)
const menuItems = computed(() => {
  const list = contactsList.value || [];
  const selectable =
    inputType.value === INPUT_TYPES.TEL
      ? list.filter(item => item.phoneNumber)
      : list;
  return [...selectable, ...createContactItem.value];
});

const selectedContactLabel = computed(() => {
  const { name, email = '', phoneNumber = '' } = props.selectedContact || {};
  if (email) {
    return `${name} (${email})`;
  }
  if (phoneNumber) {
    return `${name} (${phoneNumber})`;
  }
  return name || '';
});

const errorClass = computed(() => {
  return props.hasErrors
    ? '[&_input]:placeholder:!text-n-ruby-9 [&_input]:dark:placeholder:!text-n-ruby-9'
    : '';
});

const handleInput = value => {
  // Smarticks: anything that looks like a phone number ("052-545-7077", not only "+…")
  // is typed as 'tel'; otherwise 'email' (upstream only checked for a leading '+')
  searchQuery.value = value;
  hasSearched.value = false;
  inputType.value = looksLikePhone(value) ? INPUT_TYPES.TEL : INPUT_TYPES.EMAIL;
  emit('searchContacts', value);
};
</script>

<template>
  <div class="relative flex-1 px-4 py-3 overflow-y-visible">
    <div class="flex items-baseline w-full gap-3 min-h-7">
      <label class="text-sm font-medium text-n-slate-11 whitespace-nowrap">
        {{ t(`${i18nPrefix}.LABEL`) }}
      </label>

      <div
        v-if="isCreatingContact"
        class="flex items-center gap-1.5 rounded-md bg-n-alpha-2 px-3 min-h-7 min-w-0"
      >
        <span class="text-sm truncate text-n-slate-12">
          {{ t(`${i18nPrefix}.CONTACT_CREATING`) }}
        </span>
      </div>
      <div
        v-else-if="selectedContact"
        class="flex items-center gap-1.5 rounded-md bg-n-alpha-2 min-h-7 min-w-0"
        :class="!contactId ? 'ltr:pl-3 rtl:pr-3 ltr:pr-1 rtl:pl-1' : 'px-3'"
      >
        <span class="text-sm truncate text-n-slate-12">
          {{
            isCreatingContact
              ? t(`${i18nPrefix}.CONTACT_CREATING`)
              : selectedContactLabel
          }}
        </span>
        <Button
          v-if="!contactId"
          variant="ghost"
          icon="i-lucide-x"
          color="slate"
          :disabled="contactId"
          size="xs"
          @click="emit('clearSelectedContact')"
        />
      </div>
      <TagInput
        v-else
        :placeholder="t(`${i18nPrefix}.TAG_INPUT_PLACEHOLDER`)"
        mode="single"
        :menu-items="menuItems"
        :show-dropdown="showContactsDropdown"
        :is-loading="isLoading"
        :disabled="contactableInboxesList?.length > 0 && showInboxesDropdown"
        allow-create
        :type="inputType"
        class="flex-1 min-h-7"
        :class="errorClass"
        focus-on-mount
        @input="handleInput"
        @on-click-outside="emit('updateDropdown', 'contacts', false)"
        @add="emit('setSelectedContact', $event)"
        @remove="emit('clearSelectedContact')"
      />
    </div>
  </div>
</template>
